<template>
	<PCard>
		<template #title>
			<div class="flex align-items-center justify-content-between">
				<WTitle text="Users" />
				<PButton
					@click="openAdd()"
					icon="pi pi-plus"
					rounded
					size="small"
					v-tooltip.left="`Add a new user`"
				></PButton>
			</div>
		</template>
		<template #content>
			<PDataTable
				:alwaysShowPaginator="false"
				:loading="loading"
				:rowHover="true"
				:rows="100"
				:showGridlines="true"
				:stripedRows="true"
				:value="list"
				data-key="id"
				paginator
				size="small"
			>
				<template #empty>No users found.</template>

				<PColumn field="username" header="Username" headerClass="py-3" sortable></PColumn>
				<PColumn field="role_name" header="Role" headerClass="py-3" sortable>
					<template #body="{ data }">
						<span>{{ data.role_name || '—' }}</span>
					</template>
				</PColumn>
				<PColumn header="Status" headerClass="py-3" style="width: 110px;">
					<template #body="{ data }">
						<PTag :severity="data.active ? 'success' : 'danger'">
							{{ data.active ? 'Active' : 'Disabled' }}
						</PTag>
					</template>
				</PColumn>
				<PColumn header="Password" headerClass="py-3" style="width: 140px;">
					<template #body="{ data }">
						<PTag v-if="data.must_change_password" severity="warning">Reset pending</PTag>
						<span v-else>—</span>
					</template>
				</PColumn>
				<PColumn field="last_login" header="Last login" headerClass="py-3">
					<template #body="{ data }">
						<span>{{ formatDate(data.last_login) }}</span>
					</template>
				</PColumn>
				<PColumn bodyClass="text-right" headerClass="py-3" style="width: 100px;">
					<template #body="{ data }">
						<PButton
							@click="openEdit(data)"
							icon="pi pi-pencil"
							rounded
							severity="success"
							size="small"
							text
							v-tooltip.bottom="`Edit`"
						></PButton>
						<PButton
							@click="doDelete($event, data.id)"
							icon="pi pi-trash"
							rounded
							severity="danger"
							size="small"
							text
							v-tooltip.bottom="`Delete`"
						></PButton>
					</template>
				</PColumn>
			</PDataTable>
		</template>
	</PCard>

	<!-- Add/Edit dialog -->
	<PDialog
		:header="editing ? 'Edit user' : 'Add a new user'"
		@hide="closeDialog()"
		modal
		style="width: 28rem; max-width: 95vw;"
		v-model:visible="showDialog"
	>
		<div class="flex flex-column gap-4">
			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="username">Username</label>
				<PInputText
					:disabled="editing"
					autocomplete="off"
					id="username"
					size="small"
					v-model="item.username"
				></PInputText>
			</div>

			<div class="flex flex-column gap-2" v-if="!editing">
				<label class="text-primary-50" for="password">Password</label>
				<PInputText
					autocomplete="new-password"
					id="password"
					size="small"
					type="password"
					v-model="item.password"
				></PInputText>
			</div>

			<div class="flex flex-column gap-2" v-if="editing">
				<label class="text-primary-50" for="newpassword">New password (leave blank to keep)</label>
				<PInputText
					autocomplete="new-password"
					id="newpassword"
					size="small"
					type="password"
					v-model="item.password"
				></PInputText>
			</div>

			<div class="flex flex-column gap-2">
				<label class="text-primary-50" for="role">Role</label>
				<PDropdown
					:options="roles"
					id="role"
					optionLabel="name"
					optionValue="id"
					placeholder="Select a role"
					v-model="item.role_id"
				></PDropdown>
			</div>

			<div class="flex align-items-center gap-2" v-if="editing">
				<PInputSwitch input-id="active" v-model="item.active"></PInputSwitch>
				<label for="active">Active</label>
			</div>

			<div class="flex align-items-center justify-content-end gap-3">
				<PButton
					@click="editing ? saveUpdates() : doAdd()"
					label="Save"
					size="small"
				></PButton>
				<PButton
					@click="closeDialog()"
					label="Cancel"
					outlined
					severity="secondary"
					size="small"
				></PButton>
			</div>
		</div>
	</PDialog>

	<PConfirmDialog />
</template>

<script lang="ts">
	import { defineComponent } from 'vue'
	import BEBroker from '@/brokers/BEBroker'
	import Utils from '@/utils/Utils'
	import Constants from '@/helpers/Constants'

	export default defineComponent({
		name: 'UsersView',

		data() {
			return {
				component: 'users',
				loading: true,
				showDialog: false,
				editing: false,
				list: [] as any[],
				roles: [] as any[],
				item: {
					id: -1,
					username: '',
					password: '',
					role_id: null as number | null,
					active: true,
				},
			}
		},

		async mounted() {
			Utils.generatePid(Constants.OPID.READ)
			await this.loadRoles()
			await this.refreshList()
			this.loading = false
		},

		methods: {
			formatDate(value: string) {
				if (!value) return '—'
				const d = new Date(value)
				return isNaN(d.getTime()) ? value : d.toLocaleString()
			},

			async loadRoles() {
				const res = await BEBroker.get('roles')
				this.roles = (res && res.length) ? res : []
			},

			async refreshList() {
				const res = await BEBroker.get(this.component)
				this.list = (res && res.length) ? res : []
			},

			openAdd() {
				this.editing = false
				this.item = { id: -1, username: '', password: '', role_id: null, active: true }
				this.showDialog = true
			},

			openEdit(data: any) {
				this.editing = true
				this.item = {
					id: data.id,
					username: data.username,
					password: '',
					role_id: data.role_id,
					active: !!data.active,
				}
				this.showDialog = true
			},

			closeDialog() {
				this.showDialog = false
				this.editing = false
			},

			async doAdd() {
				if (this.item.username.trim() === '' || this.item.password === '') {
					Utils.showErrorMessage('Username and password are required.')
					return
				}
				if (this.item.password.length < 8) {
					Utils.showErrorMessage('Password must be at least 8 characters.')
					return
				}

				this.loading = true
				Utils.generatePid(Constants.OPID.ADD)
				const payload = {
					username: this.item.username.trim(),
					password: this.item.password,
					role_id: this.item.role_id,
				}
				const res = await BEBroker.add(this.component, payload)
				if (res) {
					this.closeDialog()
					await this.refreshList()
					Utils.showSuccessMessage('User has been added successfully.')
				}
				this.loading = false
			},

			async saveUpdates() {
				this.loading = true
				Utils.generatePid(Constants.OPID.UPDATE)
				const payload: any = {
					id: this.item.id,
					role_id: this.item.role_id,
					active: this.item.active ? 1 : 0,
				}
				if (this.item.password) {
					if (this.item.password.length < 8) {
						Utils.showErrorMessage('Password must be at least 8 characters.')
						this.loading = false
						return
					}
					payload.password = this.item.password
				}
				const res = await BEBroker.update(this.component, payload)
				if (res) {
					this.closeDialog()
					await this.refreshList()
					Utils.showSuccessMessage('User has been updated successfully.')
				}
				this.loading = false
			},

			async doDelete(event: any, id: number) {
				this.$confirm.require({
					target: event.currentTarget,
					header: 'Delete',
					message: 'Are you sure you want to delete this user?',
					icon: 'pi pi-info-circle',
					rejectClass: 'p-button-primary p-button-outlined p-button-sm',
					acceptClass: 'p-button-danger p-button-sm',
					rejectLabel: 'Cancel',
					acceptLabel: 'Delete',
					accept: async () => {
						this.loading = true
						Utils.generatePid(Constants.OPID.DELETE)
						await BEBroker.delete(this.component, id)
						await this.refreshList()
						this.loading = false
						Utils.showSuccessMessage('User has been deleted successfully.')
					},
				})
			},
		},
	})
</script>
